var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Fragment } from 'react';
import { useForm } from 'react-hook-form';
import { boolean, object } from 'yup';
import { Button, Checkbox, Select } from '@paysera/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useGetAccountNumberQuery } from '@app/api/accountApi';
import { useAcceptTermsAndConditionsMutation } from '@app/api/termsAndConditionsApi';
import { getInvestorLocale } from '@app/utils/getInvestorLocale';
const schema = object()
    .shape({
    investor_tc_terms: boolean().oneOf([true]),
    investor_tc_privacy: boolean().oneOf([true]),
    investor_tc_consent: boolean().oneOf([true]),
    investor_tc_marketing: boolean(),
})
    .required();
const getLegalUrl = (locale) => `https://www.paysera.com/v2/${locale}/legal`;
const TermsAndConditions = ({ agreements }) => {
    const { register, handleSubmit, formState: { errors }, } = useForm({
        resolver: yupResolver(schema),
    });
    const { t } = useTranslation();
    const { data: accountNumber } = useGetAccountNumberQuery();
    const [acceptTerms, { isLoading }] = useAcceptTermsAndConditionsMutation();
    const onSubmit = (data) => __awaiter(void 0, void 0, void 0, function* () {
        const dataToSubmit = Object.keys(data).filter((key) => data[key]);
        yield acceptTerms(dataToSubmit);
    });
    const locale = getInvestorLocale();
    return (<form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-wrap gap-6 items-start">
                <div className="flex-1 min-w-[320px] flex flex-col gap-6">
                    <div className="flex flex-col gap-4">
                        <h3 className="title-m">{t('financing.investing_page.welcome_to')}</h3>
                        <p className="body-l">{t('financing.investing_page.join_service')}</p>
                    </div>

                    <div className="flex flex-col gap-4">
                        <h3 className="title-m">{t('financing.investing_page.how_it_works')}</h3>
                        <ol className="list-decimal pl-6 body-l">
                            <li>
                                <span className="font-semibold">
                                    {t('financing.investing_page.explore_loan_title')}:
                                </span>{' '}
                                {t('financing.investing_page.explore_loan_description')}
                            </li>
                            <li>
                                <span className="font-semibold">
                                    {t('financing.investing_page.invest_loans_title')}:
                                </span>{' '}
                                {t('financing.investing_page.invest_loans_description')}
                            </li>
                            <li>
                                <span className="font-semibold">
                                    {t('financing.investing_page.earn_returns_title')}:
                                </span>{' '}
                                {t('financing.investing_page.earn_returns_description')}
                            </li>
                        </ol>

                        <div className="body-l">
                            <p className="font-semibold">{t('financing.investing_page.more_info')}</p>
                            <a className="text-action-primary-initial underline" href={getLegalUrl(locale)} target="_blank" rel="noreferrer">
                                {t('financing.investing_page.legal_info')}
                            </a>
                        </div>

                        <Select label={t('financing.investing_page.account')} options={accountNumber ? [{ value: accountNumber, label: accountNumber }] : []} value={accountNumber} disabled isClearable={false}/>
                    </div>
                </div>

                <div className="flex-1 min-w-80 max-w-md rounded-lg p-6 flex flex-col gap-4 bg-soft-gradient">
                    <div className="flex flex-col gap-2">
                        {agreements === null || agreements === void 0 ? void 0 : agreements.map((agreement, index) => (<Fragment key={agreement.name}>
                                <Checkbox variant={(errors === null || errors === void 0 ? void 0 : errors[agreement.name]) ? 'critical' : 'default'} {...register(agreement.name)} label={<>
                                            {`${t(`financing.investing_page.${agreement.name}_part1`)}`}
                                            {(agreement === null || agreement === void 0 ? void 0 : agreement.url) && (<>
                                                    {' '}
                                                    <a href={agreement.url} target="_blank" className="text-action-primary-initial hover:underline" rel="noreferrer">
                                                        <span className="inline-block [&::first-letter]:uppercase">
                                                            {`${t(`financing.investing_page.${agreement.name}`)}`}
                                                        </span>
                                                    </a>
                                                </>)}
                                        </>}/>
                                {index === 2 && Object.keys(errors).length > 0 && (<p className="text-critical-primary-initial body-s">
                                        {t('financing.investing_page.terms_error')}
                                    </p>)}
                            </Fragment>))}
                    </div>

                    <div>
                        <Button type="submit" variant="primary" isFullWidth loading={isLoading} label={t('financing.investing_page.proceed')}/>
                    </div>
                </div>
            </div>
        </form>);
};
export default TermsAndConditions;
